import React, { useContext, createRef, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { VendorContext } from '../../../../contexts/vendorContext'
import { VendorUser } from '../../../../models/VendorUser'
import EditDetailsForm from './editDetailsForm'
import { editProfile } from '../../../../services/vendorService'
import { toast } from 'react-toastify'
import constants from '../../../../config/constants'
import { friendlyMessageFor } from '../../../../utility/errorHelper'
import { firebaseAuth } from '../../../../services/firebase'
import { FirebaseError, AppError } from '../../../../config/enums'

type EditDetailsModalProps = {
  show: boolean
  handleClose: () => void
}

function EditDetailsModal({ show, handleClose }: EditDetailsModalProps) {
  const vendorContext = useContext(VendorContext)
  const wrapper = createRef<HTMLDivElement>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  let pendingOps = 0

  const addPendingOps = () => {
    pendingOps += 1
    setIsSubmitting(pendingOps > 0)
  }

  const removePendingOps = () => {
    pendingOps -= 1
    setIsSubmitting(pendingOps > 0)
  }

  return (
    <div ref={wrapper}>
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title data-testid="modal-title">Edit my details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <EditDetailsForm
            profile={vendorContext?.profile as VendorUser}
            isSubmitting={isSubmitting}
            callback={async (
              firstName: string,
              lastName: string,
              avatarUrl?: string,
              currentPass?: string,
              newPass?: string
            ) => {
              let hasChanges = false

              // Update profile if the values has changed
              if (
                firstName !== vendorContext?.profile?.firstName ||
                lastName !== vendorContext?.profile?.lastName ||
                avatarUrl !== vendorContext?.profile?.avatarUrl
              ) {
                hasChanges = true
                addPendingOps()

                editProfile(firstName, lastName, avatarUrl)
                  .then((updatedProfile) => {
                    if (updatedProfile) {
                      vendorContext?.setProfile(updatedProfile)
                      removePendingOps()
                      toast.success(
                        constants.alertMessage.docUpdated.replace(
                          '%s',
                          'My details'
                        )
                      )
                      handleClose()
                    } else {
                      // @TODO: figure out the case where this will happen
                      // For now display generic error message
                      const message = friendlyMessageFor(null)
                      toast.error(message, { autoClose: false })
                      removePendingOps()
                    }
                  })
                  .catch((err) => {
                    console.error('Vendor edit profile error:', err)
                    toast.error(err, { autoClose: false })
                    removePendingOps()
                  })
              }

              // Change password if both passwords are provided
              if (currentPass && newPass) {
                hasChanges = true
                addPendingOps()

                const user = firebaseAuth().currentUser as firebase.User
                const creds = firebaseAuth.EmailAuthProvider.credential(
                  user.email as string,
                  currentPass
                )

                user
                  .reauthenticateWithCredential(creds)
                  .then(() => {
                    return user?.updatePassword(newPass)
                  })
                  .then(() => {
                    removePendingOps()
                    toast.success(constants.alertMessage.passwordUpdated)
                    handleClose()

                    // Relogin to firebase to preserve auth state
                    return firebaseAuth().signInWithEmailAndPassword(
                      user.email as string,
                      newPass
                    )
                  })
                  .catch((err) => {
                    let message = friendlyMessageFor(err.code)
                    if (err.code === FirebaseError.authWrongPassword) {
                      message = friendlyMessageFor(
                        AppError.currentPasswordIncorrect
                      )
                    }
                    toast.error(message, { autoClose: false })
                    removePendingOps()
                  })
              }

              // If there are no changes, simply dismiss the form
              if (!hasChanges) {
                handleClose()
              }
            }}
          />
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default EditDetailsModal
